// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';

// material ui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Chat feedback component with dialog-based rating
 * @param {Object} props
 * @param {Function} props.onFeedback - Callback (feedback: 'positive' | 'negative', comments?: string) => void
 * @param {boolean} props.disabled - Disable button
 */
export const ChatFeedback = ({ onFeedback, disabled = false }) => {
  const { t } = useTranslation('ai-assistant');
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [additionalComments, setAdditionalComments] = useState('');

  const handleOpenDialog = () => {
    if (disabled || feedbackGiven) return;
    setDialogOpen(true);
  };

  const handleSelectFeedback = (type) => {
    setSelectedFeedback(type);
  };

  const handleSubmitFeedback = () => {
    if (!selectedFeedback) return;

    // Pass feedback and optional comments to parent
    onFeedback(selectedFeedback, additionalComments || undefined);

    setFeedbackGiven(true);
    setDialogOpen(false);
    // Don't reset selectedFeedback/comments to preserve state
  };

  const handleCancelFeedback = () => {
    setDialogOpen(false);
    setSelectedFeedback(null);
    setAdditionalComments('');
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
        }}
      >
        {feedbackGiven ? (
          <Typography variant="caption" color="text.secondary">
            {t('feedback.thanks')}
          </Typography>
        ) : (
          <Button
            variant="text"
            size="small"
            onClick={handleOpenDialog}
            disabled={disabled}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            {t('feedback.button')}
          </Button>
        )}
      </Box>

      {/* Feedback Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelFeedback}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('feedback.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('feedback.prompt')}
            </Typography>

            {/* Rating Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <IconButton
                  size="large"
                  onClick={() => handleSelectFeedback('positive')}
                  color={
                    selectedFeedback === 'positive' ? 'success' : 'default'
                  }
                  sx={{
                    border: (theme) =>
                      selectedFeedback === 'positive'
                        ? `2px solid ${theme.palette.success.main}`
                        : `2px solid ${theme.palette.divider}`,
                    '&:hover': {
                      color: 'success.main',
                      borderColor: 'success.main',
                    },
                  }}
                >
                  {selectedFeedback === 'positive' ? (
                    <ThumbUpIcon fontSize="large" />
                  ) : (
                    <ThumbUpOutlinedIcon fontSize="large" />
                  )}
                </IconButton>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {t('feedback.helpful')}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <IconButton
                  size="large"
                  onClick={() => handleSelectFeedback('negative')}
                  color={selectedFeedback === 'negative' ? 'error' : 'default'}
                  sx={{
                    border: (theme) =>
                      selectedFeedback === 'negative'
                        ? `2px solid ${theme.palette.error.main}`
                        : `2px solid ${theme.palette.divider}`,
                    '&:hover': {
                      color: 'error.main',
                      borderColor: 'error.main',
                    },
                  }}
                >
                  {selectedFeedback === 'negative' ? (
                    <ThumbDownIcon fontSize="large" />
                  ) : (
                    <ThumbDownOutlinedIcon fontSize="large" />
                  )}
                </IconButton>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {t('feedback.notHelpful')}
                </Typography>
              </Box>
            </Box>

            {/* Optional Comments */}
            {selectedFeedback && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {selectedFeedback === 'positive'
                    ? t('feedback.commentPromptPositive')
                    : t('feedback.commentPromptNegative')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={t('feedback.commentPlaceholder')}
                  value={additionalComments}
                  onChange={(e) => setAdditionalComments(e.target.value)}
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelFeedback} color="inherit">
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={handleSubmitFeedback}
            variant="contained"
            disabled={!selectedFeedback}
            color={
              selectedFeedback === 'positive'
                ? 'success'
                : selectedFeedback === 'negative'
                  ? 'error'
                  : 'primary'
            }
          >
            {t('actions.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
